import { lazy, useState, useEffect } from "react";
import { Redirect, Route, Switch } from "react-router-dom";
import { useSetRecoilState } from "recoil";

import Activation from "../pages/activation/index";
import CreateOrLogin from "../pages/interstitial/createOrLogin";
import CreateOrLoginTikTok from "../pages/interstitial/createOrLoginTikTok";
import StudioMarketingPage from "../pages/studioMarketing/index";
import Verify from "../pages/verify/index";

import Edit from "../MVVM/Components/EditClipOrMontage";
import TikTokEmail from "../MVVM/Views/Authentication/Components/TikTokEmail";
import { getChunkable } from "./utils";
import { URLS } from "../MVVM/Utilities/Maps";
import { useGetUser } from "../userStore";
import Error404 from "../MVVM/Views/404";
import Homepage from "../MVVM/Views/Homepage";
import { ProfileRedirect } from "../MVVM/Components/ProfileRedirect";

import { redirects } from "./redirects";
import { externalLinks } from "./externalLinks";
import { authenticationPages } from "./authenticationPages";
import { brandPages } from "./brandPages";
import { mobileWebviews } from "./mobileWebviews";
import { staticPages } from "./staticPages";
import { getQueryParam } from "MVVM/Utilities/QueryParam";
import { ProContentExperimentState } from "MVVM/State/embeddablePlaylist";
import { isPartner, Partner } from "MVVM/Views/EmbeddablePlaylist/Utilities";
import { TimelineEditorPage } from "MVVM/Views/TimelineEditor";

const LazyClipExperiencePage = lazy(() =>
  import("../MVVM/Views/ClipExperience"),
);
const LazyMontagesHaveMoved = lazy(() =>
  import("../MVVM/Views/CreateMontage/Components/MontagesHaveMoved/index"),
);
const LazyFeatureMontage = lazy(() => import("../MVVM/Views/FeatureMontage"));
const LazyMobileAuth = lazy(() => import("../MVVM/Views/MobileAuth"));
const LazyMatchHistory = lazy(() => import("../MVVM/Views/MatchHistory"));
const LazyPricing = lazy(() => import("../MVVM/Views/Pricing"));
const LazyWelcome = lazy(() => import("../MVVM/Views/Welcome"));
const LazyStudioPage = lazy(() => import("../MVVM/Views/Studio"));
const LazyAddShareCodes = lazy(() => import("../MVVM/Views/AddShareCodes"));
const LazyAccount = lazy(() => import("../pages/profileSettings"));
const LazyPaymentValidation = lazy(() =>
  import("../MVVM/Views/PaymentValidation"),
);
const LazyMyLibrary = lazy(() => import("../MVVM/Views/MyLibrary"));
const LazyProfile = lazy(() => import("../MVVM/Views/Profile"));

const LazyCompetitionHub = lazy(() => import("../MVVM/Views/Competitions/Hub"));
const LazyCreateCompetition = lazy(() =>
  import("../MVVM/Views/Competitions/Create"),
);
const LazyCompetitionDetail = lazy(() =>
  import("../MVVM/Views/Competitions/Detail"),
);
const LazyCompetitionEntry = lazy(() =>
  import("../MVVM/Views/Competitions/Entry"),
);
const LazyCompetitionContestant = lazy(() =>
  import("../MVVM/Views/Competitions/Contestant"),
);
const LazyMyCompetitions = lazy(() =>
  import("../MVVM/Views/Competitions/MyCompetitions"),
);
const LazyPrizesCheckout = lazy(() =>
  import("../MVVM/Views/Competitions/PrizesCheckout"),
);
const LazyCompetitionManage = lazy(() =>
  import("../MVVM/Views/Competitions/Manage"),
);

const LazyNewUserHomepage = lazy(() =>
  import("../MVVM/Views/LandingPages/NewUserHomepage"),
);

const LazyAutoCapture = lazy(() =>
  import("../MVVM/Views/LandingPages/AutoCapture"),
);
const LazyAllstarCommand = lazy(() =>
  import("../MVVM/Views/LandingPages/AllstarCommand"),
);

const LazyIframeDemo = lazy(() => import("../MVVM/Views/IframeDemo"));
const LazyAdsDemo = lazy(() => import("../MVVM/Views/AdsDemo"));
// Note: /demo/hltv-preview is registered in App.js above <Main /> so it
// bypasses the Allstar app chrome (header, sidebar). Do not re-add here.
const LazyHLTVSkinGiveaway = lazy(() =>
  import("../MVVM/Views/HLTVSkinGiveaway"),
);

const LazyTeamsHub = lazy(() => import("../MVVM/Views/TeamsHub"));
const LazyTeam = lazy(() => import("../MVVM/Views/Team"));
const LazyBlogList = lazy(() => import("../MVVM/Views/Blog"));
const LazyBlogPost = lazy(() =>
  import("../MVVM/Views/Blog").then((module) => ({
    default: module.BlogPost,
  })),
);

const PrivateRoute = ({ bypassAuth, ...props }) => {
  const user = useGetUser();
  if (!user && !bypassAuth?.()) {
    return <Redirect to={`/login?next=${window.location.pathname}`} />;
  }
  return <Route {...props} />;
};

const MontagesGatedRoute = () => {
  return getChunkable(LazyMontagesHaveMoved)();
};

const AppRoutes = () => {
  const [isModalOpen, setModalOpen] = useState(false);
  const showModal = () => setModalOpen(!isModalOpen);
  const clipId = getQueryParam("clip") || getQueryParam("montage");
  const setShowProContent = useSetRecoilState(ProContentExperimentState);

  const searchParams = new URLSearchParams(window.location.search);
  const utmParam = searchParams.get("utm_source");
  const isHLTV = isPartner(utmParam, Partner.HLTV);

  useEffect(() => {
    if (isHLTV) setShowProContent(true);
  }, [isHLTV, setShowProContent]);

  const ProfilePage = getChunkable(LazyProfile);

  const ProfilePageWithKey = (props) => {
    const { user } = props.match.params;
    return <ProfilePage key={user} {...props} />;
  };

  return (
    <>
      <Switch>
        {authenticationPages}
        {externalLinks}
        {mobileWebviews}
        {staticPages}
        {brandPages}
        <Route exact path="/" component={Homepage} />
        <Route
          exact
          path="/advanced-creation/:matchId"
          component={TimelineEditorPage}
        />
        <Route
          path="/wrapped/2024/:user"
          render={() => <Homepage wrappedPeriod="2024" />}
        />
        <Route exact path="/start" render={getChunkable(LazyNewUserHomepage)} />
        <Route
          exact
          path={URLS.AUTOCAPTURE}
          render={getChunkable(LazyAutoCapture)}
        />
        <Route
          exact
          path={URLS.ALLSTAR_COMMAND}
          render={getChunkable(LazyAllstarCommand)}
        />
        <Route
          exact
          path="/iframe-demo"
          render={getChunkable(LazyIframeDemo)}
        />
        <Route exact path="/ads-demo" render={getChunkable(LazyAdsDemo)} />
        <Route exact path="/hltv" render={getChunkable(LazyHLTVSkinGiveaway)} />
        <PrivateRoute
          exact
          path="/library"
          component={getChunkable(LazyMyLibrary)}
        />
        <Route exact path="/u/:user" component={ProfilePageWithKey} />
        <Route
          exact
          path="/profile"
          render={(props) => <ProfileRedirect {...props} />}
        />
        <Route
          path={"/match-history"}
          exact
          component={getChunkable(LazyMatchHistory)}
        />
        {/*
        <Route
          path={"/match-history/:page"}
          exact
          component={getChunkable(LazyMatchHistory)}
        /> */}
        <Route
          path={"/remix/:momentId"}
          render={getChunkable(LazyStudioPage, { route: "remix" })}
        />
        <Route
          path={"/studio/:cardId"}
          render={getChunkable(LazyStudioPage, { route: "studio" })}
        />
        <Route
          path={"/studio"}
          component={getChunkable(LazyStudioPage, {
            route: "studio",
          })}
        />
        <Route exact path="/activation/download-app" component={Activation} />
        <Route exact path="/activation/verify" component={Activation} />
        <Route exact path="/verify" component={Verify} />
        <Route
          exact
          path="/activation/connect-discord"
          render={(props) => <Activation {...props} step={2} />}
        />
        <Route
          exact
          path="/clip/:id"
          component={getChunkable(LazyClipExperiencePage)}
        />
        <Route key="clipRedirect" path="/clip">
          {({ location }) => {
            const searchParams = new URLSearchParams(location.search);
            searchParams.delete("clip");

            const redirectPath = `clip/${clipId}${
              searchParams ? `?${searchParams.toString()}` : ""
            }`;

            return <Redirect to={redirectPath} />;
          }}
        </Route>
        <Route key="montageRedirect" path="/montage">
          <Redirect to={`clip/${clipId}`} />
        </Route>
        <Route path="/edit/:shareId" component={Edit}></Route>
        <Route
          exact
          path="/montages/create"
          render={() => <MontagesGatedRoute />}
        />
        <Route exact path="/mobile-auth" component={LazyMobileAuth} />
        <Route exact path="/interstitial" component={CreateOrLogin} />
        <Route
          exact
          path="/interstitialtiktok"
          component={CreateOrLoginTikTok}
        />
        <Route exact path="/finishsignup" component={TikTokEmail} />
        <Route
          exact
          path="/feature/allstarstudio"
          component={StudioMarketingPage}
        />
        <Route
          exact
          path="/feature/montages"
          component={getChunkable(LazyFeatureMontage)}
        />
        <Route
          exact
          path={URLS.UPGRADE}
          component={getChunkable(LazyPricing)}
        />
        <Route path="/welcome" render={getChunkable(LazyWelcome)} />
        <Route
          exact
          path="/competitions"
          render={getChunkable(LazyCompetitionHub)}
        />
        <Route
          exact
          path="/competitions/create"
          render={getChunkable(LazyCreateCompetition)}
        />
        <Route
          exact
          path="/competitions/mine"
          render={getChunkable(LazyMyCompetitions)}
        />
        <Route
          exact
          path="/competitions/:slug/enter"
          render={getChunkable(LazyCompetitionEntry)}
        />
        <Route
          exact
          path="/competitions/:slug/contestant/:userId"
          render={getChunkable(LazyCompetitionContestant)}
        />
        <Route
          exact
          path="/competitions/:slug/prizes/checkout"
          render={getChunkable(LazyPrizesCheckout)}
        />
        <Route
          exact
          path="/competitions/:slug/manage"
          render={getChunkable(LazyCompetitionManage)}
        />
        <Route
          exact
          path="/competitions/:slug"
          render={getChunkable(LazyCompetitionDetail)}
        />
        <Route exact path="/teams" render={getChunkable(LazyTeamsHub)} />
        <Route
          exact
          path="/team/:slug/:joinCode"
          component={getChunkable(LazyTeam)}
        />
        <Route exact path="/team/:slug" component={getChunkable(LazyTeam)} />
        <PrivateRoute
          exact
          path="/notifications"
          render={getChunkable(LazyAccount, {
            showModal,
            view: "notifications",
          })}
        />
        <PrivateRoute
          exact
          path="/third-party-settings"
          render={getChunkable(LazyAccount, {
            showModal,
            view: "thirdPartySettings",
          })}
        />
        <PrivateRoute
          exact
          path="/steam-bot"
          render={getChunkable(LazyAccount, {
            showModal,
            view: "steambotSettings",
          })}
        />
        <PrivateRoute
          exact
          path="/accountsettings"
          render={getChunkable(LazyAccount, {
            showModal,
            view: "accountSettings",
          })}
        />
        <PrivateRoute
          exact
          path="/connectedaccounts"
          render={getChunkable(LazyAccount, {
            showModal,
            view: "connectedAccounts",
          })}
        />
        <PrivateRoute
          exact
          path="/subscriptions"
          render={getChunkable(LazyAccount, {
            showModal,
            view: "subscriptions",
          })}
        />
        <PrivateRoute
          path={"/payment/validation"}
          exact
          component={getChunkable(LazyPaymentValidation)}
        />
        <PrivateRoute
          path={"/add-share-codes"}
          exact
          component={getChunkable(LazyAddShareCodes)}
        />
        {redirects}
        <Route exact path="/blog" component={getChunkable(LazyBlogList)} />
        <Route
          exact
          path="/blog/:slug"
          component={getChunkable(LazyBlogPost)}
        />
        <Route exact path="/404" component={Error404} />
        <Route path="*" component={Error404} />
      </Switch>
    </>
  );
};

export { AppRoutes };
